// Hofstra University: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Hofstra's Common Data Set 2025-2026, which Institutional Research and
//    Strategic Analysis links from its Facts and Figures page as an Issuu
//    document (2025-2026_common_data_set_-_hofstra_university, 42 pages; the
//    SOURCES url is the hofstra.edu page that links it; the page images were
//    read, and C1 checked by eye): C1 applicants, admits and enrollees for
//    Fall 2025 with the residency table (4 applicants and 1 admit of unknown
//    residency are in the totals but have no row here); C8 test policy for
//    Fall 2027; C21/C22 plans with both Early Action rounds dated for this
//    cycle. The CDS G1 is marked as not final (2026-2027 costs "not available
//    at this time", due July 2026), so it is not used.
//  - Hofstra Admission: Application Process (the 2027 deadlines and decision
//    dates), FAQ (Early Action rules, special programs), Standardized Testing
//    Policy and What Does it Cost (the 2026-27 cost of attendance).
//  - College Scorecard, school id 191649 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Quirk: Hofstra's Counselors page labels its two early rounds "Early
// Decision 1" and "Early Decision 2" while describing them as non-binding
// Early Action; the CDS and the FAQ say Hofstra has no early decision.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Hofstra Common Data Set 2025-2026',
        url: 'https://www.hofstra.edu/about/administration/institutional-research-strategic-analysis/facts-figures/',
        read: '2026-10-01',
    },
    apply: {
        label: 'Hofstra Admission, Application Process',
        url: 'https://www.hofstra.edu/admission/apply/',
        read: '2026-10-01',
    },
    faq: {
        label: 'Hofstra Admission, Frequently Asked Questions',
        url: 'https://www.hofstra.edu/admission/faq.html',
        read: '2026-10-01',
    },
    tests: {
        label: 'Hofstra Admission, Standardized Testing Policy',
        url: 'https://www.hofstra.edu/admission/standardized-testing-policy/',
        read: '2026-10-01',
    },
    costs: {
        label: 'Hofstra Admission, What Does it Cost',
        url: 'https://www.hofstra.edu/admission/what-does-it-cost/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?191649-Hofstra-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'hofstra-university',
    kbId: 'hofstra_university',
    name: 'Hofstra University',
    shortName: 'Hofstra',
    location: 'Hempstead, NY',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Hofstra athletics brand guide); decoration only.
    branding: {
        primary: '#003594',
        secondary: '#FFC72C',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/hofstra_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 21771,
        admitted: 15821,
        enrolled: 1615,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 11963, admitted: 9051 },
            { group: 'Out-of-state', applied: 8143, admitted: 6667 },
            { group: 'International', applied: 1661, admitted: 102 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Hofstra\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Hofstra has no early decision plan. It has two non-binding, non-restrictive Early Action rounds: the Common Data Set lists Early Action I closing November 15, 2026 with notification on December 15, 2026, and Early Action II closing December 15, 2026 with notification on January 15, 2027. It does not report how many applied or were admitted early.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Hofstra as a first-year student. Some programs work differently:',
        rows: [
            { school: 'Physician Assistant Studies', requirement: 'A dual-degree program: a BS and an MS in five and a half years. Admission is open only to first-year students and is highly selective.', source: 'faq' },
            { school: 'Programs that require test scores', requirement: 'SAT or ACT scores are required for the Physician Assistant Studies dual-degree program and the Hofstra 4+4 BS-BA/MD program with the Zucker School of Medicine, and for students who want the Trustee Scholarship. No other dual-degree program requires them.', source: 'tests' },
            { school: 'Legal Education Accelerated Program (LEAP)', requirement: 'A six-year combined BA and JD: three years in Hofstra College of Liberal Arts and Sciences, then three at Hofstra Law School.', source: 'faq' },
            { school: 'NOAH (New Opportunities at Hofstra)', requirement: 'For New York residents who are TAP-eligible, economically disadvantaged and in a state-recognized high-needs school district. Apply by February 1; NOAH requires an interview and financial documents before a decision.', source: 'faq' },
            { school: 'PALS (Program for Academic Learning Skills)', requirement: 'For students with documented learning disabilities. The committee reviews your credentials with psychological testing to decide on an interview, and decides after the interview.', source: 'faq' },
            { school: 'Rabinowitz Honors College', requirement: 'Highly selective, for top-performing first-year students. If you send scores, Hofstra recommends a 1340 SAT or a 29 ACT.', source: 'faq' },
        ],
        source: 'faq',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Early Action I', value: 'November 15' },
        rows: [
            { plan: 'Early Action I', deadline: 'November 15', decision: 'By December 15', binding: false },
            { plan: 'Early Action II', deadline: 'December 15', decision: 'By January 15', binding: false },
            { plan: 'Regular Decision', deadline: 'Rolling, no deadline', decision: 'Rolling, from February 1', binding: false },
        ],
        note: 'Apply for fall 2027 with the Common App. Regular Decision is rolling: Hofstra reviews applications as they come in, and decisions go out on a rolling basis beginning February 1.',
        source: 'apply',
        binding: 'Early Action is not binding: Hofstra is an Early Action school, not an Early Decision school. The Early Action I deadline is November 15 and the Early Action II deadline is December 15, and you can still apply after both, since Hofstra takes applications all year on a rolling basis.',
        bindingSource: 'faq',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Hofstra is test-optional for full-time first-year applicants. If you send SAT or ACT scores, Hofstra superscores all of them; if you apply test-optional, scores already sent are not considered. Scores are required from homeschooled and online high school students, international students whose native language is not English (the TOEFL or the SAT or ACT), Physician Assistant Studies and BS-BA/MD applicants, and Trustee Scholarship candidates. An interview is strongly encouraged without scores, and for some students required.',
        source: 'tests',
        cdsLine: 'The Common Data Set marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores taken on a rolling basis.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition and fees', amount: 62110 },
            { item: 'Food and housing (room and board, on campus)', amount: 20850 },
            { item: 'Books and supplies', amount: 1000 },
            { item: 'Personal expenses', amount: 1050 },
            { item: 'Transportation', amount: 1000 },
            { item: 'Student loan fees', amount: 68 },
            { item: 'Parent PLUS loan fees', amount: 846 },
        ],
        total: 86924,
        note: 'For a full-time first-year student living on campus. Hofstra says these figures are subject to change.',
        source: 'costs',
        netPrice: {
            average: 30647,
            byIncome: [
                { income: '$0 to $30,000', amount: 23816 },
                { income: '$30,001 to $48,000', amount: 26489 },
                { income: '$48,001 to $75,000', amount: 30441 },
                { income: '$75,001 to $110,000', amount: 34218 },
                { income: 'Over $110,000', amount: 34880 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Hofstra\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
